import { notify } from '@affine/component';
import { GraphQLService } from '@affine/core/modules/cloud';
import { hasAppBaseErrorCode } from '@affine/core/modules/streamlain/hooks/app-base-error-code';
import { ViewService } from '@affine/core/modules/workbench/services/view';
import { WorkbenchService } from '@affine/core/modules/workbench/services/workbench';
import { publishAppContainerToMineMutation } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService, useServiceOptional } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import type {
  ContainerTemplatePublishSource,
  TemplatePublishSource,
} from './publish-source';

/** Stable identity shared by editor entry points and the private Hub detail. */
export function getHubPublishSourceKey(source: TemplatePublishSource): string {
  switch (source.kind) {
    case 'container':
      return `container:${source.appBaseId}`;
    case 'doc':
      return `doc:${source.docId}`;
    case 'app':
      return `app:${source.appBaseId}:${source.appId}`;
    case 'workflow':
      return `workflow:${source.workflowId}`;
    case 'automation':
      return `automation:${source.automationBlockId}`;
    case 'step':
      return `step:${source.workflowId}:${source.stepId}`;
  }
}

/** Editor Publish buttons prepare the same content in Mine before review. */
export function PublishHubEntry({
  open,
  onClose,
  input,
}: {
  open: boolean;
  onClose: () => void;
  input: TemplatePublishSource;
}) {
  const workbench = useService(WorkbenchService).workbench;
  const view = useServiceOptional(ViewService)?.view;
  const entered = useRef(false);

  useEffect(() => {
    if (!open) {
      entered.current = false;
      return;
    }
    if (input.kind === 'container') return;
    if (entered.current) return;
    entered.current = true;
    const query = new URLSearchParams({
      tab: 'published',
      detail: getHubPublishSourceKey(input),
      publishEntry: 'source',
    });
    if (input.initialVisibility)
      query.set('publishVisibility', input.initialVisibility);
    // Close the old entry before navigating, so returning to a retained
    // editor never opens the publication flow again.
    onClose();
    if (view) workbench.active(view);
    workbench.open(`/marketplace?${query}`, { at: 'active' });
  }, [input, onClose, open, view, workbench]);

  return input.kind === 'container' ? (
    <ContainerPublishHubEntry open={open} onClose={onClose} input={input} />
  ) : null;
}

/** Containers publish privately as one operation before opening the resulting listing. */
function ContainerPublishHubEntry({
  open,
  onClose,
  input,
}: {
  open: boolean;
  onClose: () => void;
  input: ContainerTemplatePublishSource;
}) {
  const graphql = useService(GraphQLService);
  const workbench = useService(WorkbenchService).workbench;
  const view = useServiceOptional(ViewService)?.view;
  const t = useI18n();
  const started = useRef(false);
  useEffect(() => {
    if (!open) {
      started.current = false;
      return;
    }
    if (started.current) return;
    started.current = true;
    void graphql
      .gql({
        query: publishAppContainerToMineMutation,
        variables: {
          workspaceId: input.workspaceId,
          appBaseId: input.appBaseId,
        },
      })
      .then(result => {
        const publication = result.publishAppContainerToMine;
        const query = new URLSearchParams({
          tab: 'published',
          detail: `publication:${publication.templateId}`,
        });
        onClose();
        if (view) workbench.active(view);
        workbench.open(`/marketplace?${query}`, { at: 'active' });
      })
      .catch(error => {
        const key = hasAppBaseErrorCode(
          error,
          'MARKETPLACE_PUBLISH_OWNER_REQUIRED'
        )
          ? 'ownerRequired'
          : hasAppBaseErrorCode(error, 'MARKETPLACE_PUBLISH_DAILY_LIMIT')
            ? 'dailyLimit'
            : hasAppBaseErrorCode(
                  error,
                  'MARKETPLACE_CONTAINER_NO_MOUNTED_APPS'
                )
              ? 'empty'
              : hasAppBaseErrorCode(
                    error,
                    'MARKETPLACE_CONTAINER_DUPLICATE_APP'
                  )
                ? 'duplicateApp'
                : 'failed';
        const message = hasAppBaseErrorCode(
          error,
          'MARKETPLACE_PUBLISH_PLAN_UNAVAILABLE'
        )
          ? t.t('com.streamlain.hub.mine.publish-unavailable')
          : t.t(`com.streamlain.marketplace.container.${key}`);
        notify.error({ title: message });
        onClose();
      });
  }, [
    graphql,
    input.appBaseId,
    input.workspaceId,
    onClose,
    open,
    t,
    view,
    workbench,
  ]);
  return null;
}
